import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Input, Alert } from "@repo/ui";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import LocationPicker from "../../../../components/LocationPicker/LocationPicker";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import withAuth from "../../../../hoc/withAuth/withAuth";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";
import { gsap } from "gsap";
import NavigationButtons from "../../components/NavigationButtons";

const LOCALE =
  "modules_Registration_pages_CreateGiftTable_CreateGiftTableStep3";

const CreateGiftTableStep3: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const giftTable = useSelector((state: RootState) => state.giftTable);
  const [eventLocation, setEventLocation] = useState(
    giftTable.eventLocation || { address: "", lat: 0, lng: 0 }
  );
  const [eventPlace, setEventPlace] = useState(giftTable.eventPlace || "");
  const [alert, setAlert] = useState<{
    message: React.ReactNode;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (alertRef.current) {
      gsap.fromTo(
        alertRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 0.5 }
      );
    }
  }, [alert]);

  useEffect(() => {
    setEventLocation(
      giftTable.eventLocation || { address: "", lat: 0, lng: 0 }
    );
    setEventPlace(giftTable.eventPlace || "");
  }, [giftTable.eventLocation, giftTable.eventPlace]);

  const handleContinue = () => {
    if (!eventPlace || !eventLocation.address) {
      setAlert({ message: t("completeAllFieldsError"), level: "warn" });
      return;
    }

    dispatch(updateGiftTableState({ eventLocation, eventPlace }));
    navigate("/registration/create-gift-table/step4");
  };

  const handleBack = () => {
    navigate("/registration/create-gift-table/step2");
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={3}
        progressPercentage={50}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        {alert && (
          <div ref={alertRef}>
            <Alert message={alert.message} level={alert.level} />
          </div>
        )}
        <div className="flex flex-col space-y-4">
          <label htmlFor="eventPlace" className="text-label font-paragraph">
            {t("eventPlaceNameLabel")}
          </label>
          <Input
            value={eventPlace}
            onChange={(e) => setEventPlace(e.target.value)}
            type="text"
            className="input-border"
          />
          <label htmlFor="eventLocation" className="text-label font-paragraph">
            {t("eventLocationLabel")}
          </label>
          <LocationPicker
            value={eventLocation.address}
            onChange={(location) => setEventLocation(location)}
          />
          <NavigationButtons
            continueLabel={t("continueButton")}
            backLabel={t("backButton", { defaultValue: "Regresar" })}
            onContinue={handleContinue}
            onBack={handleBack}
            className="w-full"
          />
        </div>
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(CreateGiftTableStep3));
